import { type Temporal, useFormatDateTime } from "@hermes/intl";
import { useFocusRing } from "react-aria";

import {
  TriggerButton,
  type TriggerChildrenProps,
} from "../../button/trigger-button";
import { createStaticStyles, cssFns } from "../../css";
import { DatePicker } from "../../date-picker";
import { colors } from "../../theme";
import { FieldDescription } from "../common/description";
import { FieldError } from "../common/error";
import { BaseFieldInput, BaseFieldLayout } from "../common/field";
import { type Field, type FieldState, useBaseFieldState } from "../common/form";
import { FieldLabel } from "../common/label";

type DateFieldValue = Temporal.PlainDate;

export type DateFieldState = FieldState<DateFieldValue | null>;

const staticStyles = {
  button: createStaticStyles.clean([
    cssFns.margin("0px"),
    cssFns.border({ width: "0px", style: "none" }),
    cssFns.padding("0px", "10px"),
    {
      width: "100%",
      height: "100%",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      backgroundColor: "transparent",
      outlineStyle: "none",
    },
  ]),
  value: createStaticStyles.clean([
    cssFns.typo({
      level: "body-1",
      weight: "regular",
    }),
    {
      overflowX: "hidden",
      whiteSpace: "nowrap",
      textOverflow: "ellipsis",
      color: colors.principal.gs2,
    },
  ]),
};

export function DateField({
  label,
  placeholder,
  description,
  field,
  minDate,
  maxDate,
  children,
  onChange,
}: {
  label?: string;
  placeholder?: string;
  description?: string;
  field: Field<DateFieldState>;
  minDate?: Temporal.PlainDate;
  maxDate?: Temporal.PlainDate;
  onChange?: (date?: Temporal.PlainDate) => void;
} & TriggerChildrenProps) {
  const formatDateTime = useFormatDateTime();
  const { isFocused, focusProps } = useFocusRing({
    isTextInput: false,
    within: true,
  });

  return (
    <DatePicker
      value={field.value ?? undefined}
      onChange={(value) => {
        onChange?.(value);
        field.onChange({
          ...field,
          value: value ?? null,
        });
      }}
      minDate={minDate}
      maxDate={maxDate}
      disabled={field.disabled}
      placement="bottom start"
    >
      {({ triggerRef, triggerProps }) => (
        <BaseFieldLayout
          label={
            label && (
              <FieldLabel disabled={field.disabled} required={field.required}>
                {label}
              </FieldLabel>
            )
          }
          input={
            children ? (
              children({ triggerRef, triggerProps })
            ) : (
              <BaseFieldInput
                field={field}
                focused={isFocused}
                fieldProps={focusProps}
                input={
                  <TriggerButton
                    ref={triggerRef}
                    triggerProps={triggerProps}
                    styles={staticStyles.button}
                  >
                    <div
                      css={{
                        display: "grid",
                      }}
                    >
                      <span css={staticStyles.value}>
                        {field.value
                          ? formatDateTime(field.value)
                          : placeholder}
                      </span>
                    </div>
                  </TriggerButton>
                }
                disabled={field.disabled}
              />
            )
          }
          description={
            description && (
              <FieldDescription disabled={field.disabled}>
                {description}
              </FieldDescription>
            )
          }
          error={
            field.error?.visible && (
              <FieldError
                fieldRequired={field.required}
                errorMessage={field.error.message}
              />
            )
          }
        />
      )}
    </DatePicker>
  );
}

export const useDateFieldState = (defaultState?: DateFieldState) => {
  return useBaseFieldState<DateFieldState>({
    value: null,
    error: undefined,
    ...defaultState,
  });
};
